import { ChevronDown, ChevronUp, Wand2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import ErrorContent from "@/components/Canvas/ErrorOverlay/ErrorContent";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { track } from "@/utils/events/tracker";
import { clearAllInternalErrors } from "@/store/iframeSlice";
import { useDispatch } from "react-redux";
import { Tooltip } from "react-tooltip";
import {
  DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { useStartServer } from "@/utils/devServerUtils";

interface ErrorsBlockProps {
  errors: DevErrorJson[];
  isLastMessage: boolean;
}

export function ErrorsBlock({ errors, isLastMessage }: ErrorsBlockProps) {
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );
  const numFixWithAiMessages = useAiContextStore(
    (state) => state.numFixWithAiMessages
  );

  const isChatLoading = useIsChatLoading();
  const startServer = useStartServer();

  const [isExpanded, setIsExpanded] = useState(false);

  const clearStoryboardIdToErrors = useDevServerStore(
    (state) => state.clearStoryboardIdToErrors
  );
  const clearTerminalErrors = useDevServerStore(
    (state) => state.clearTerminalErrors
  );
  const dispatch = useDispatch();

  if (!errors.length) return null;

  return (
    <div className="flex flex-col pr-1.5 !mt-[-0.2rem] !mb-1.5">
      <Alert
        variant="destructive"
        className="flex flex-col gap-3 p-2 animate-in fade-in-0 zoom-in-95 bg-background-primary !border-red-600/60 shrink-0 w-full !pl-2"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <AlertDescription className="text-sm font-semibold text-text-primary pl-1">
              Potential Problems Detected
            </AlertDescription>
          </div>
        </div>
        <div
          className="flex flex-col gap-2 cursor-pointer border rounded-sm p-1 hover:bg-background-secondary"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between w-full cursor-pointer">
            <div className="flex items-center justify-between w-full pr-1">
              <AlertDescription className="text-xs font-medium text-text-primary flex flex-row gap-1.5 items-center">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-600/20 text-error-400 text-xs font-medium">
                  {errors.length}
                </div>
                <span>{isExpanded ? "Hide" : "Show"}</span>{" "}
                <span>Problems</span>
              </AlertDescription>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-text-secondary" />
              ) : (
                <ChevronDown className="h-4 w-4 text-text-secondary" />
              )}
            </div>
          </div>
          {isExpanded && (
            <div className="space-y-2 pl-1">
              {errors.map((error, index) => (
                <ErrorContent
                  message={error.message}
                  filename={error.filename}
                  lineno={error.lineno}
                  colno={error.colno}
                  stack={error.stack}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div
            data-tooltip-id="fix-limit-tooltip"
            data-tooltip-html="<div class='text-xs max-w-[200px]'>You've used Fix with AI 7 times in a row. Consider asking the AI for a more creative way to fix the problem.</div>"
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const prompt = `Please make a code change to fix the above errors in my code.`;
                const sent = setSendChatMessage({
                  sendChatMessage: prompt,
                  isFixWithAi: true,
                  includeErrorsIfAny: true,
                  isFreePrompt: true,
                  refreshAfterResponse: true,
                });
                if (sent) {
                  track("AI_PROMPT_REQUESTED", {
                    type: "AI_CHAT_FIX_ERROR",
                    prompt,
                    isFreePrompt: true,
                  });
                }
              }}
              disabled={
                isChatLoading || !isLastMessage || numFixWithAiMessages >= 7
              }
              className="h-8 text-2xs gap-1.5 bg-background-secondary hover:bg-background-tertiary text-text-primary"
            >
              <Wand2 className="h-3.5 w-3.5" />
              Fix with AI
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              startServer({
                forceRestart: true,
              })
            }
            className="h-8 text-2xs gap-1.5 bg-background-secondary hover:bg-background-tertiary text-text-primary"
          >
            Restart Server
          </Button>
          {numFixWithAiMessages >= 7 && <Tooltip id="fix-limit-tooltip" />}
          <Button
            variant="secondary"
            size="sm"
            disabled={isChatLoading || !isLastMessage}
            onClick={() => {
              clearStoryboardIdToErrors();
              dispatch(clearAllInternalErrors());
              clearTerminalErrors();
            }}
            className="h-8 text-2xs bg-background-secondary hover:bg-background-tertiary text-text-primary"
          >
            Clear
          </Button>
        </div>
      </Alert>
    </div>
  );
}
